/**
 * ViewOrderDialog Component
 * Displays complete order details in a read-only modal dialog
 * Shows all order information including items, taxes, delivery, and payment
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-006 Phase 5
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import orderService from '@/services/commerce/orderService';
import type { OrderDetails, OrderStatus } from '@/types/order';

// ============================================
// TYPES
// ============================================

export interface ViewOrderDialogProps {
  orderId: number | null;
  isOpen: boolean;
  onClose: () => void;
}

// ============================================
// CONSTANTS
// ============================================

const STATUS_BADGE_COLORS: Record<OrderStatus, string> = {
  ORDER_INITIALIZED: 'bg-slate-100 text-slate-800',
  PENDING: 'bg-yellow-100 text-yellow-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  IN_PROGRESS: 'bg-purple-100 text-purple-800',
  READY: 'bg-cyan-100 text-cyan-800',
  IN_TRANSIT: 'bg-orange-100 text-orange-800',
  DELIVERED: 'bg-emerald-100 text-emerald-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800',
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

function formatCurrency(amount: number): string {
  return `₹${amount.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(dateString: string | undefined): string {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return 'Invalid Date';
  }
}

// ============================================
// COMPONENT
// ============================================

export function ViewOrderDialog({
  orderId,
  isOpen,
  onClose,
}: ViewOrderDialogProps): JSX.Element {
  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !orderId) {
      setOrder(null);
      setError(null);
      return;
    }

    const fetchOrder = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const orderData = await orderService.getOrderById(orderId);
        setOrder(orderData);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load order details';
        setError(errorMessage);
        console.error('Error fetching order:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrder();
  }, [orderId, isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Order Details</DialogTitle>
          <DialogClose />
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Loading order details...</span>
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {order && !isLoading && (
          <div className="space-y-6">
            {/* Order Header */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-xs text-gray-500 uppercase">Order Number</p>
                <p className="text-lg font-semibold text-gray-900">
                  {order.orderNumber}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Status</p>
                <Badge className={STATUS_BADGE_COLORS[order.orderStatus]}>
                  {order.orderStatus}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Order Date</p>
                <p className="text-sm font-medium text-gray-900">
                  {formatDate(order.orderDate)}
                </p>
              </div>
            </div>

            {/* Customer Information */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Customer Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Customer Name</p>
                  <p className="text-sm font-medium text-gray-900">
                    {order.customer.customerName}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="text-sm font-medium text-gray-900">
                    {order.customer.email || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Phone</p>
                  <p className="text-sm font-medium text-gray-900">
                    {order.customer.phone || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">GST Number</p>
                  <p className="text-sm font-medium text-gray-900">
                    {order.customer.gstNumber || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Line Items */}
            {order.lineItems && order.lineItems.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Order Items
                </h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-2 text-left font-semibold">
                          Product Name
                        </th>
                        <th className="px-4 py-2 text-left font-semibold">
                          Batch Code
                        </th>
                        <th className="px-4 py-2 text-right font-semibold">
                          Quantity
                        </th>
                        <th className="px-4 py-2 text-right font-semibold">
                          Unit Price
                        </th>
                        <th className="px-4 py-2 text-right font-semibold">
                          Line Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.lineItems.map((item, index) => (
                        <tr key={index} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-2">
                            <div>
                              <p className="font-medium text-gray-900">
                                {item.productName}
                              </p>
                              {item.description && (
                                <p className="text-xs text-gray-500 mt-1">
                                  {item.description}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-2">
                            <p className="text-sm font-medium text-gray-900">
                              {item.batchCode ? (
                                <Badge variant="outline">{item.batchCode}</Badge>
                              ) : (
                                <span className="text-xs text-gray-400">-</span>
                              )}
                            </p>
                          </td>
                          <td className="px-4 py-2 text-right">
                            <div>
                              <p className="font-medium">{item.quantity}</p>
                              <p className="text-xs text-gray-500">
                                {item.quantityUnit || 'Units'}
                              </p>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-right">
                            {formatCurrency(item.unitPrice)}
                          </td>
                          <td className="px-4 py-2 text-right font-medium">
                            {formatCurrency(item.lineTotal)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Tax Breakdown */}
            {order.gstBreakdown && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Tax Breakdown
                </h3>
                <div className="space-y-2 p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium">
                      {formatCurrency(order.gstBreakdown.subtotal)}
                    </span>
                  </div>
                  {order.gstBreakdown.cgstAmount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">CGST (9%)</span>
                      <span className="font-medium">
                        {formatCurrency(order.gstBreakdown.cgstAmount)}
                      </span>
                    </div>
                  )}
                  {order.gstBreakdown.sgstAmount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">SGST (9%)</span>
                      <span className="font-medium">
                        {formatCurrency(order.gstBreakdown.sgstAmount)}
                      </span>
                    </div>
                  )}
                  {order.gstBreakdown.igstAmount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">IGST (18%)</span>
                      <span className="font-medium">
                        {formatCurrency(order.gstBreakdown.igstAmount)}
                      </span>
                    </div>
                  )}
                  <div className="border-t pt-2 flex justify-between">
                    <span className="font-semibold text-gray-900">Total</span>
                    <span className="font-bold text-lg">
                      {formatCurrency(order.gstBreakdown.totalAmount)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Delivery Information */}
            {order.delivery && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Delivery Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Delivery Address</p>
                    <p className="text-sm font-medium text-gray-900">
                      {order.delivery.address?.addressLine1 || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">
                      Expected Delivery Date
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      {formatDate(order.delivery.expectedDeliveryDate)}
                    </p>
                  </div>
                  {order.delivery.actualDeliveryDate && (
                    <div>
                      <p className="text-xs text-gray-500">
                        Actual Delivery Date
                      </p>
                      <p className="text-sm font-medium text-gray-900">
                        {formatDate(order.delivery.actualDeliveryDate)}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-gray-500">Contact Person</p>
                    <p className="text-sm font-medium text-gray-900">
                      {order.delivery.contactPerson || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Contact Number</p>
                    <p className="text-sm font-medium text-gray-900">
                      {order.delivery.contactPhone || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Information */}
            {order.payment && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Payment Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Total Amount</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatCurrency(order.payment.totalAmount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Paid Amount</p>
                    <p className="text-sm font-medium text-gray-900">
                      {formatCurrency(order.payment.paidAmount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Balance Due</p>
                    <p className="text-sm font-medium">
                      {order.payment.balanceDue > 0 ? (
                        <span className="text-red-600">
                          {formatCurrency(order.payment.balanceDue)}
                        </span>
                      ) : (
                        <span className="text-emerald-600">Paid</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Payment Status</p>
                    <p className="text-sm font-medium text-gray-900">
                      {order.payment.paymentStatus}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ViewOrderDialog;
